/**
 * LogoPill — Fragments logo in a rounded container with 3D depth.
 *
 * Usage:
 *   <LogoPill />            → 64px default
 *   <LogoPill size={80} />  → 80px
 */
import * as React from 'react';
import { FragmentsLogo } from '@fragments-sdk/ui';

function pillStyle(size: number): React.CSSProperties {
  const r = size * 0.24;
  return {
    width: size,
    height: size,
    borderRadius: r,
    color: '#f8fafc',
    background: `
      linear-gradient(
        165deg,
        rgba(255, 255, 255, 0.14) 0%,
        rgba(255, 255, 255, 0.05) 38%,
        rgba(0, 0, 0, 0.28) 100%
      ),
      linear-gradient(
        145deg,
        #2a2d35,
        #171a21
      )
    `,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    boxShadow: [
      // Outer depth
      `0 ${size * 0.06}px ${size * 0.15}px rgba(15, 23, 42, 0.3)`,
      `0 ${size * 0.02}px ${size * 0.04}px rgba(0, 0, 0, 0.35)`,
      // Top highlight rim
      `inset 0 1px 0 rgba(255, 255, 255, 0.12)`,
      // Inner bevel
      `inset 0 -1px 2px rgba(0, 0, 0, 0.28)`,
    ].join(', '),
  };
}

function sheenStyle(size: number): React.CSSProperties {
  const r = size * 0.24;
  return {
    position: 'absolute',
    inset: 0,
    borderRadius: r,
    background:
      'linear-gradient(160deg, rgba(255, 255, 255, 0.06) 0%, transparent 50%)',
    pointerEvents: 'none',
  };
}

export function LogoPill({ size = 64 }: { size?: number }) {
  return (
    <div style={pillStyle(size)}>
      <div style={sheenStyle(size)} />
      <FragmentsLogo size={Math.round(size * 0.5)} />
    </div>
  );
}
